import { isPlatformBrowser } from '@angular/common';
import type { OnDestroy, OnInit } from '@angular/core';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, PLATFORM_ID } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { RouterOutlet } from '@angular/router';
import { RealityCheckDialogComponent } from '@app/components/dialogs/reality-check-dialog/reality-check-dialog.component';
import { Header } from '@app/components/header/header';
import { IosInstallNudge } from '@app/components/ios-install-nudge/ios-install-nudge';
import { CountdownBarComponent } from '@app/features/countdown-bar/components/countdown-bar/countdown-bar';
import { OneSignalService } from '@app/services/one-signal/one-signal';
import { RealityCheckService } from '@app/services/reality-check/reality-check';

@Component({
  selector: 'bingo-private',
  imports: [RouterOutlet, Header, IosInstallNudge, CountdownBarComponent],
  templateUrl: './private.view.html',
  styleUrl: './private.view.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PrivateView implements OnDestroy, OnInit {
  private readonly realityCheckService = inject(RealityCheckService);
  private readonly dialog = inject(MatDialog);
  private readonly oneSignalService = inject(OneSignalService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly platformId = inject(PLATFORM_ID);

  ngOnInit(): void {
    this.realityCheckService.start();

    if (isPlatformBrowser(this.platformId)) {
      void this.initPush();
    }

    this.realityChecksDialog();
  }

  ngOnDestroy(): void {
    this.realityCheckService.stop();
  }

  /*
     init() runs unconditionally so external-id and tags are set for targeting;
     the permission prompt itself is deferred to the first user gesture (required
     by iOS Safari, and far better opt-in behaviour everywhere else).
  */
  private async initPush(): Promise<void> {
    await this.oneSignalService.enablePushOnUserGesture();
  }

  private realityChecksDialog(): void {
    this.realityCheckService.trigger$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(note => {
        const alreadyOpen = this.dialog.openDialogs.some(
          ref => ref.componentInstance instanceof RealityCheckDialogComponent,
        );

        if (alreadyOpen) return;

        this.dialog.open(RealityCheckDialogComponent, {
          data: { note },
          disableClose: true,
          width: 'min(400px, 95vw)',
        });
      });
  }
}
